import React, { useContext, useState } from 'react';
import UserContext from '../../context/UserContext.js';
import CampoComboParametro from '../UI/campos/CampoComboParametro.js';
import BotonInsertar from '../UI/botones/BotonInsertar.js';
import ColumnaBotoneraItem from '../edicion/ColumnaBotoneraItem.js';
import MensajeError from '../UI/MensajeError.js';
import MensajeGrabando from '../UI/MensajeGrabando.js';
import SelectEquipoAfectado from '../selectores/SelectEquipoAfectado.js';
import SelectResolucionResultado from '../selectores/SelectResolucionResultado.js';
import * as TipoParametro from '../constantes/TipoParametro.js';
import * as Data from '../data/DataPartidos.js';
import { calcularResultadosPartido } from '../funciones/FuncionesResultadoPartido.js';
import styles from './ResultadosSecundarios.module.css';

const ResultadoVacio = {
  tipoResultado: null,
  minutos: null,
  golesLocal: null,
  codResultadoLocal: '',
  golesVisitante: null,
  codResultadoVisitante: '',
  equipoAfectado: null,
  codResolucion: null,
  observaciones: '',
};

const valorMarcador = (resultado, localia) => {
  const goles = localia === 'local' ? resultado.golesLocal : resultado.golesVisitante;
  const codigo =
    localia === 'local' ? resultado.codResultadoLocal : resultado.codResultadoVisitante;
  if (goles !== null && goles !== undefined && codigo) return `${goles} (${codigo})`;
  return goles ?? codigo ?? '';
};

const tieneValor = (valor) =>
  valor !== null && valor !== undefined && (typeof valor !== 'string' || valor.trim() !== '');

const validarResultado = (resultado, resultados, idEnEdicion) => {
  const erroresValidacion = [];
  const codigoTipo = resultado.tipoResultado?.codigo;

  if (!tieneValor(codigoTipo)) {
    erroresValidacion.push('El tipo de resultado es obligatorio.');
  } else {
    const tipoDuplicado = resultados.some((item) => {
      const idItem = item.id ?? item.idPartidoResultado;
      const codigoItem = (item.tipoResultado || item.tipo || item.resultadoTipo)?.codigo;
      return Number(idItem) !== Number(idEnEdicion) && Number(codigoItem) === Number(codigoTipo);
    });
    if (tipoDuplicado) {
      erroresValidacion.push('Ya existe otro resultado de este tipo en el partido.');
    }
  }

  const tieneGolesLocal = tieneValor(resultado.golesLocal);
  const tieneGolesVisitante = tieneValor(resultado.golesVisitante);
  if (tieneGolesLocal !== tieneGolesVisitante) {
    erroresValidacion.push('Debe informar los goles de ambos equipos o dejar ambos vacíos.');
  }

  const tieneResultadoLocal = tieneValor(resultado.codResultadoLocal);
  const tieneResultadoVisitante = tieneValor(resultado.codResultadoVisitante);
  if (tieneResultadoLocal !== tieneResultadoVisitante) {
    erroresValidacion.push('Debe informar el resultado de ambos equipos o dejar ambos vacíos.');
  }

  return erroresValidacion;
};

const CampoTexto = ({ nombre, etiqueta, valor, maxLength, inputMode, onChange }) => (
  <label className={styles.campo}>
    <span>{etiqueta}</span>
    <input
      aria-label={etiqueta}
      type="text"
      inputMode={inputMode}
      maxLength={maxLength}
      value={valor ?? ''}
      onChange={(event) => {
        const texto =
          inputMode === 'numeric' ? event.target.value.replace(/\D/g, '') : event.target.value;
        const normalizado = texto.slice(0, maxLength);
        onChange(
          nombre,
          inputMode === 'numeric' ? (normalizado === '' ? null : Number(normalizado)) : normalizado
        );
      }}
    />
  </label>
);

const ResultadosSecundarios = ({
  idPartido,
  resultados = [],
  partido,
  onResultadosActualizados,
  onPartidoActualizado,
}) => {
  const { usuario } = useContext(UserContext);
  const puedeEditar = Boolean(usuario && usuario.puedeEditar);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [idEnEdicion, setIdEnEdicion] = useState(null);
  const [resultado, setResultado] = useState({ ...ResultadoVacio });
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState(null);

  const actualizarCampo = (campo, valor) => {
    setResultado((actual) => ({ ...actual, [campo]: valor }));
  };

  const iniciarAlta = () => {
    setResultado({ ...ResultadoVacio });
    setIdEnEdicion(null);
    setErrores(null);
    setDialogoAbierto(true);
  };

  const iniciarEdicion = (id) => {
    const existente = resultados.find(
      (item) => Number(item.id ?? item.idPartidoResultado) === Number(id)
    );
    if (!existente) return;

    setResultado({
      ...ResultadoVacio,
      ...existente,
      tipoResultado: existente.tipoResultado || existente.tipo || existente.resultadoTipo || null,
    });
    setIdEnEdicion(id);
    setErrores(null);
    setDialogoAbierto(true);
  };

  const cerrarDialogo = () => {
    if (guardando) return;
    setDialogoAbierto(false);
    setIdEnEdicion(null);
    setResultado({ ...ResultadoVacio });
    setErrores(null);
  };

  const recargarResultados = async () => {
    const actualizados = await Data.FetchResultados(idPartido);
    const lista = actualizados || [];
    onResultadosActualizados(lista);
    return lista;
  };

  // El backend actualiza resultadoLocal/resultadoVisitante en la base; acá solo se
  // refleja el cambio en pantalla sin volver a cargar el partido.
  const sincronizarResultadoPrincipal = (resultadosActuales) => {
    if (!partido || !onPartidoActualizado) return;
    const calculados = calcularResultadosPartido(
      partido.golesLocal,
      partido.golesVisitante,
      resultadosActuales
    );
    if (!calculados) return;
    onPartidoActualizado({ ...partido, ...calculados });
  };

  const guardarResultado = async (event) => {
    event.preventDefault();
    setErrores(null);
    const erroresValidacion = validarResultado(resultado, resultados, idEnEdicion);
    if (erroresValidacion.length > 0) {
      setErrores(erroresValidacion);
      return;
    }

    setGuardando(true);
    try {
      if (idEnEdicion === null) {
        await Data.PostResultado(idPartido, resultado);
      } else {
        await Data.PutResultado(idPartido, { ...resultado, id: idEnEdicion });
      }
      const resultadosActualizados = await recargarResultados();
      sincronizarResultadoPrincipal(resultadosActualizados);
      setDialogoAbierto(false);
      setIdEnEdicion(null);
      setResultado({ ...ResultadoVacio });
    } catch (error) {
      setErrores([error.message || 'No se pudo guardar el resultado']);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarResultado = async (id) => {
    if (!window.confirm('¿Está seguro que desea eliminar este resultado?')) return;
    setErrores(null);
    setGuardando(true);
    try {
      await Data.DeleteResultado(idPartido, id);
      await recargarResultados();
    } catch (error) {
      setErrores([error.message || 'No se pudo eliminar el resultado']);
    } finally {
      setGuardando(false);
    }
  };

  const resultadosOrdenados = [...resultados].sort((a, b) => {
    const codigoA = (a.tipoResultado || a.tipo || a.resultadoTipo)?.codigo;
    const codigoB = (b.tipoResultado || b.tipo || b.resultadoTipo)?.codigo;
    return Number(codigoA ?? 0) - Number(codigoB ?? 0);
  });

  return (
    <React.Fragment>
      <div className={styles.listaResultados}>
        <table className={styles.tablaResultados} aria-label="Resultados secundarios">
          <tbody>
            {resultadosOrdenados.map((item, index) => {
              const id = item.id ?? item.idPartidoResultado ?? index;
              return (
                <tr className={styles.filaResultado} key={id}>
                  <td className={styles.tipoResultado}>
                    {item.tipoResultado?.descripcion ||
                      item.tipo?.descripcion ||
                      item.descripcionTipoResultado ||
                      item.descripcion}
                  </td>
                  <td className={styles.marcador}>{valorMarcador(item, 'local')}</td>
                  <td className={styles.separador}>-</td>
                  <td className={styles.marcador}>{valorMarcador(item, 'visitante')}</td>
                  {puedeEditar && (
                    <ColumnaBotoneraItem
                      idItem={id}
                      onEditarItem={iniciarEdicion}
                      onEliminarItem={eliminarResultado}
                      className={styles.botonera}
                    />
                  )}
                  {puedeEditar && index === resultadosOrdenados.length - 1 && (
                    <td className={styles.insertarEnFila}>
                      <BotonInsertar
                        onClick={iniciarAlta}
                        estilo={styles.botonInsertar}
                        estiloImagen={styles.imgInsertar}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {puedeEditar && resultados.length === 0 && (
        <div className={styles.insertarResultado}>
          <button type="button" className={styles.enlaceAgregar} onClick={iniciarAlta}>
            Agregar resultado
          </button>
        </div>
      )}
      <MensajeGrabando isGrabando={guardando} />
      {errores && !dialogoAbierto && <MensajeError errores={errores} />}
      {dialogoAbierto && (
        <div className={styles.fondoDialogo}>
          <section
            className={styles.dialogo}
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-editor-resultado"
          >
            <h2 id="titulo-editor-resultado">
              {idEnEdicion === null ? 'Agregar resultado' : 'Editar resultado'}
            </h2>
            <form onSubmit={guardarResultado}>
              <div className={styles.camposPrincipales}>
                <label className={`${styles.campo} ${styles.tipoResultadoCampo}`}>
                  <span>Tipo Resultado</span>
                  <CampoComboParametro
                    nombre="tipoResultado"
                    tipoParametro={TipoParametro.TIPO_RESULTADO}
                    valor={resultado.tipoResultado}
                    editar={true}
                    dropUp={false}
                    estilo={styles.comboTipoResultado}
                    onChange={actualizarCampo}
                  />
                </label>
                <CampoTexto
                  nombre="minutos"
                  etiqueta="Minutos"
                  valor={resultado.minutos}
                  maxLength={3}
                  inputMode="numeric"
                  onChange={actualizarCampo}
                />
                <div className={`${styles.campo} ${styles.parValores}`}>
                  <span>Goles</span>
                  <div className={styles.valoresEnfrentados}>
                    <input
                      aria-label="Goles local"
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={resultado.golesLocal ?? ''}
                      onChange={(event) => {
                        const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
                        actualizarCampo('golesLocal', digits === '' ? null : Number(digits));
                      }}
                    />
                    <span aria-hidden="true">-</span>
                    <input
                      aria-label="Goles visitante"
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={resultado.golesVisitante ?? ''}
                      onChange={(event) => {
                        const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
                        actualizarCampo('golesVisitante', digits === '' ? null : Number(digits));
                      }}
                    />
                  </div>
                </div>
                <div className={`${styles.campo} ${styles.parValores}`}>
                  <span>Resultado</span>
                  <div className={styles.valoresEnfrentados}>
                    <input
                      aria-label="Resultado local"
                      type="text"
                      maxLength={2}
                      value={resultado.codResultadoLocal || ''}
                      onChange={(event) => actualizarCampo('codResultadoLocal', event.target.value)}
                    />
                    <span aria-hidden="true">-</span>
                    <input
                      aria-label="Resultado visitante"
                      type="text"
                      maxLength={2}
                      value={resultado.codResultadoVisitante || ''}
                      onChange={(event) =>
                        actualizarCampo('codResultadoVisitante', event.target.value)
                      }
                    />
                  </div>
                </div>
                <label className={`${styles.campo} ${styles.comboCampo}`}>
                  <span>Equipo afectado</span>
                  <SelectEquipoAfectado
                    codigoActual={resultado.equipoAfectado}
                    dropUp={true}
                    estilo={styles.comboEquipoAfectado}
                    onEquipoAfectadoElegido={(codigo) => actualizarCampo('equipoAfectado', codigo)}
                  />
                </label>
                <label className={`${styles.campo} ${styles.comboCampo}`}>
                  <span>Resolución</span>
                  <SelectResolucionResultado
                    codigoActual={resultado.codResolucion}
                    dropUp={true}
                    estilo={styles.comboResolucion}
                    onResolucionElegida={(codigo) => actualizarCampo('codResolucion', codigo)}
                  />
                </label>
              </div>
              <label className={styles.observaciones}>
                <span>Observaciones</span>
                <textarea
                  aria-label="Observaciones"
                  rows={2}
                  maxLength={255}
                  value={resultado.observaciones || ''}
                  onChange={(event) => actualizarCampo('observaciones', event.target.value)}
                />
              </label>
              <div className={styles.accionesDialogo}>
                {errores && <MensajeError estilo={styles.erroresDialogo} errores={errores} />}
                <div className={styles.botonesDialogo}>
                  <button type="submit" disabled={guardando}>
                    Guardar
                  </button>
                  <button type="button" disabled={guardando} onClick={cerrarDialogo}>
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      )}
    </React.Fragment>
  );
};

export default ResultadosSecundarios;
